import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  Input,
  EventEmitter,
  Output,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
} from '@angular/core';
import { ValueAccessorBase } from '../default-class/value-accessor';
import { NgModel, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FilterModel } from './filter.model';
import { MAT_SELECT_CONFIG, MatSelect } from '@angular/material/select';

@Component({
  selector: 'tidy-filter',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'filter.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FilterComponent,
      multi: true,
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'tidy-filter-overlay-panel' },
    },
  ],
  styleUrls: ['./filter.component.scss'],
  standalone: false,
})
export class FilterComponent
  extends ValueAccessorBase<any>
  implements OnInit, OnChanges
{
  @Input() items: FilterModel[];
  @Input() label: string;
  @Input() showLabels: boolean;
  @Input() icon: string;
  @Input() submitted: boolean;
  @Input() newValue: string | number | boolean | any[] = null;
  @Input() fitContent: boolean;
  @Input() initValue: string | number | boolean | any[];
  @Input() translate = false;
  @Input() multiple = false;
  @Input() helperText: string;
  @Output() optionChange: EventEmitter<string | any[]> = new EventEmitter<string | any[]>();
  @Input() searchPlaceholder = '';
  @ViewChild(NgModel, { static: false }) model: NgModel;
  @ViewChild('multiSelectSearchInput') multiSelectSearchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatSelect) matSelect: MatSelect;
  @ViewChild('matMultiSelect') matMultiSelect: MatSelect;

  activeClass = {};
  public selected: string;
  public loaded = false;
  touchUpdate = false;
  readonly ENABLE_SEARCH_MIN_ITEMS = 5;
  listedItems: FilterModel[];
  filteredItems: FilterModel[];
  selectedValues: any[] = [];
  searchValue = '';

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit() {
    if (this.multiple && Array.isArray(this.initValue)) {
      this.selectedValues = this.initValue;
      this.value = this.initValue;
    } else {
      this.value = this.initValue;
      this.selected = this.value;
    }
    this.activeClass['fit-content'] = this.fitContent;
    this.listedItems = this.items || [];
    this.filteredItems = [...this.listedItems];
  }

  onChange(e) {
    this.touchUpdate = true;
    setTimeout(() => this.optionChange.emit(e.value));
  }

  openPanelFromLabel() {
    const select = this.multiple ? this.matMultiSelect : this.matSelect;
    if (select && !select.panelOpen) {
      select.open();
    }
  }

  openMultiSelectFromContainer(event: MouseEvent) {
    if (!this.matMultiSelect) {
      return;
    }
    const target = event.target as Element | null;
    if (target?.closest('.mat-mdc-select, mat-select')) {
      return;
    }
    if (!this.matMultiSelect.panelOpen) {
      this.matMultiSelect.open();
    }
  }

  onMultiSelectChange(event: any): void {
    this.touchUpdate = true;
    this.selectedValues = event.value;
    this.value = event.value;
    setTimeout(() => this.optionChange.emit(event.value));
  }

  getMultiSelectDisplayValue(): string {
    if (!this.selectedValues || this.selectedValues.length === 0) {
      return this.helperText || '';
    }
    const selectedItems = this.listedItems.filter((item) =>
      this.selectedValues.includes(item.value)
    );
    if (selectedItems.length === 0) {
      return this.helperText || '';
    }
    return selectedItems.map((item) => item.viewValue).join(', ');
  }

  onMultiSelectOpened(isOpen: boolean): void {
    if (isOpen) {
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
      if (this.listedItems?.length > this.ENABLE_SEARCH_MIN_ITEMS) {
        setTimeout(() => {
          this.multiSelectSearchInput?.nativeElement?.focus();
        }, 100);
      }
    } else {
      this.searchValue = '';
      this.filteredItems = [...this.listedItems];
    }
  }

  onMultiSelectSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const searchLower = input.value.toLowerCase();
    this.searchValue = input.value;
    if (!searchLower) {
      this.filteredItems = [...this.listedItems];
    } else {
      this.filteredItems = this.listedItems.filter((item) =>
        item.viewValue?.toLowerCase().includes(searchLower)
      );
    }
  }

  clearMultiSelectSearch(): void {
    this.searchValue = '';
    this.filteredItems = [...this.listedItems];
    if (this.multiSelectSearchInput?.nativeElement) {
      this.multiSelectSearchInput.nativeElement.value = '';
      this.multiSelectSearchInput.nativeElement.focus();
    }
  }

  async ngOnChanges(_changes: SimpleChanges) {
    if (this.newValue !== null && this.newValue !== undefined) {
      if (this.multiple && Array.isArray(this.newValue)) {
        this.selectedValues = this.newValue;
        this.value = this.newValue;
      } else {
        this.value = this.newValue;
        this.selected = this.value;
      }
    }
    this.listedItems = this.items || [];
    this.filteredItems = [...this.listedItems];
  }

  searchItems(value: string) {
    this.listedItems = this.items.filter((item) =>
      item.viewValue.toLowerCase().includes(value.toLowerCase())
    );
  }
}
